'use client';

import { createContext, useContext, useCallback, ReactNode } from 'react';
import { Locale, defaultLocale } from './config';

// Import dictionaries statically (they're small JSON files)
import es from './dictionaries/es.json';
import en from './dictionaries/en.json';
import fr from './dictionaries/fr.json';
import de from './dictionaries/de.json';
import it from './dictionaries/it.json';
import pt from './dictionaries/pt.json';
import nl from './dictionaries/nl.json';
import pl from './dictionaries/pl.json';
import ru from './dictionaries/ru.json';
import sv from './dictionaries/sv.json';
import da from './dictionaries/da.json';
import ja from './dictionaries/ja.json';
import ko from './dictionaries/ko.json';
import zh from './dictionaries/zh.json';
import ar from './dictionaries/ar.json';
import hi from './dictionaries/hi.json';
import uk from './dictionaries/uk.json';
import ca from './dictionaries/ca.json';
import gl from './dictionaries/gl.json';
import eu from './dictionaries/eu.json';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Dictionary = Record<string, any>;

const dictionaries: Record<string, Dictionary> = {
  es, en, fr, de, it, pt, nl, pl, ru, sv,
  da, ja, ko, zh, ar, hi, uk, ca, gl, eu
};

// For languages not yet translated, fall back to English then Spanish
function getDictionary(locale: string): Dictionary {
  return dictionaries[locale] || dictionaries.en || dictionaries.es;
}

// Nested key access: t('home.heroTitle') => dictionary.home.heroTitle
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getNestedValue(obj: any, path: string): string {
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return path; // Return key as fallback
    }
  }
  return typeof current === 'string' ? current : path;
}

interface LanguageContextType {
  locale: Locale;
  t: (key: string, params?: Record<string, string>) => string;
  dictionary: Dictionary;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

/**
 * El idioma lo determina la URL (lo resuelve el middleware y lo inyecta el
 * layout raíz vía `initialLocale`). No hay estado de idioma en cliente: el
 * selector de idioma navega a la URL del idioma, lo que recarga la página
 * con el `initialLocale` correcto. Así el HTML servido ya está en el
 * idioma adecuado (necesario para que Google indexe cada idioma).
 */
export function LanguageProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  initialLocale?: Locale;
}) {
  const locale: Locale = initialLocale ?? defaultLocale;
  const dictionary = getDictionary(locale);

  const t = useCallback(
    (key: string, params?: Record<string, string>): string => {
      let value = getNestedValue(dictionary, key);

      // Fallback to Spanish if key not found in current locale
      if (value === key && locale !== 'es') {
        value = getNestedValue(dictionaries.es, key);
      }

      // Replace {param} placeholders
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          value = value.replace(`{${k}}`, v);
        });
      }

      return value;
    },
    [dictionary, locale],
  );

  return (
    <LanguageContext.Provider value={{ locale, t, dictionary }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
}
